import { Flex, Input, Text, useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { AuthenticatedComponent } from '../auth/keycloak';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { getUserFollowers, getUserFollowing, UserProfile } from '../service/social';
import { BrowserView } from 'react-device-detect';
import { FollowerListItem } from '../component/profileComponents/FollowerListItem';
import { HiSearch } from 'react-icons/hi';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { RadioFilter } from '../component/RadioFilter';

export const ProfileFollowers: AuthenticatedComponent = ({ keycloak }) => {
    const FOLLOWING = 'FOLLOWING';
    const FOLLOWERS = 'FOLLOWERS';
    const MUTUALS = 'MUTUALS';

    const userID = keycloak.subject;

    const toast = useToast();

    const [followers, setFollowers] = usePageDataCache<UserProfile[] | undefined>(
        'followers',
        undefined,
    );
    const [following, setFollowing] = usePageDataCache<UserProfile[] | undefined>(
        'following',
        undefined,
    );
    const [mutuals, setMutuals] = usePageDataCache<UserProfile[] | undefined>('mutuals', undefined);

    const [filteredFollowers, setFilteredFollowers] = useState<UserProfile[] | undefined>([]);
    const [filteredFollowing, setFilteredFollowing] = useState<UserProfile[] | undefined>([]);
    const [filteredMutuals, setFilteredMutuals] = useState<UserProfile[] | undefined>([]);

    const [filter, setFilter] = useState<string>(FOLLOWING);

    useEffect(() => {
        fetchFollowerInformation();
        fetchFollowingInformation();
    }, []);

    useEffect(() => {
        if (following === undefined || followers === undefined) return;

        const matches = following?.filter((p1) => {
            return followers?.find((p2) => p2.id === p1.id);
        });

        setMutuals(matches);
        setFilteredMutuals(matches);
    }, [followers, following]);

    const fetchFollowerInformation = async () => {
        try {
            const followers = await getUserFollowers(userID, keycloak.token);
            console.debug('followers', followers);
            setFollowers(followers);
            setFilteredFollowers(followers);
        } catch (e) {
            toast({
                title: 'Something Went Wrong',
                description: (e as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const fetchFollowingInformation = async () => {
        try {
            const following = await getUserFollowing(userID, keycloak.token);
            console.debug('following', following);
            setFollowing(following);
            setFilteredFollowing(following);
        } catch (e) {
            toast({
                title: 'Something Went Wrong',
                description: (e as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const updateSearch = (event: any) => {
        setFilteredFollowers(
            followers?.filter((p) =>
                p.username.toLowerCase().includes(event.target.value.toLowerCase()),
            ),
        );
        setFilteredFollowing(
            following?.filter((p) =>
                p.username.toLowerCase().includes(event.target.value.toLowerCase()),
            ),
        );
        setFilteredMutuals(
            mutuals?.filter((p) =>
                p.username.toLowerCase().includes(event.target.value.toLowerCase()),
            ),
        );
    };

    const getTitle = (): string => {
        switch (filter) {
            case FOLLOWING:
                return `FOLLOWING ${following?.length || 0}`;
            case FOLLOWERS:
                return `FOLLOWERS ${followers?.length || 0}`;
            case MUTUALS:
                return `MUTUALS ${mutuals?.length || 0}`;
        }
        return '';
    };

    const getFilteredList = (): UserProfile[] => {
        switch (filter) {
            case FOLLOWING:
                return filteredFollowing || [];
            case FOLLOWERS:
                return filteredFollowers || [];
            case MUTUALS:
                return filteredMutuals || [];
        }
        return [];
    };

    const renderFilters = () => (
        <RadioFilter
            groupName="gamelist"
            options={[
                {
                    option: FOLLOWING,
                    className: 'profile-follow-filter',
                    selectedClassName: 'profile-follow-filter-selected',
                },
                {
                    option: FOLLOWERS,
                    className: 'profile-follow-filter',
                    selectedClassName: 'profile-follow-filter-selected',
                },
                {
                    option: MUTUALS,
                    className: 'profile-follow-filter',
                    selectedClassName: 'profile-follow-filter-selected',
                },
            ]}
            defaultOption={FOLLOWING}
            value={filter}
            onRadioChange={(newValue) => setFilter(newValue)}
        />
    );

    return (
        <>
            <BrowserView>
                <Flex className={'profile-followers-content'}>
                    <Flex className="following-filters-browser">
                        {renderFilters()}
                        <Flex className="following-search">
                            <Input variant={'unstyled'} onChange={updateSearch} />
                            <Flex className={'following-search-icon'}>
                                <HiSearch size={'10px'} />
                            </Flex>
                        </Flex>
                    </Flex>
                    <Flex className={'profile-following-list'}>
                        {getFilteredList()?.length === 0 ? (
                            <Text>No results</Text>
                        ) : (
                            getFilteredList()?.map((followProfile) => (
                                <FollowerListItem key={followProfile.id} user={followProfile} />
                            ))
                        )}
                    </Flex>
                </Flex>
            </BrowserView>
            <MobileViewPage
                title={getTitle()}
                titleColor="#000"
                titleBackgroundColor="#3FA5F0"
                isLoading={followers == undefined || following === undefined}
            >
                <Flex className={'profile-followers-content'}>
                    <Flex className={'divider'} />
                    <Flex className="following-filters">
                        {renderFilters()}
                        <Flex className="following-search">
                            <Input variant={'unstyled'} onChange={updateSearch} />
                            <Flex className={'following-search-icon'}>
                                <HiSearch size={'10px'} />
                            </Flex>
                        </Flex>
                    </Flex>
                    <Flex className={'profile-following-list'}>
                        {getFilteredList()?.length === 0 ? (
                            <Text>No results</Text>
                        ) : (
                            getFilteredList()?.map((followProfile) => (
                                <FollowerListItem key={followProfile.id} user={followProfile} />
                            ))
                        )}
                    </Flex>
                </Flex>
            </MobileViewPage>
        </>
    );
};
